<template>
  <Dialog
    v-model:visible="localVisible"
    modal
    :header="title"
    :closable="true"
    class="google-maps-dialog"
  >
    <div class="h-[60vh] md:h-[70vh] w-[80vw] rounded-lg overflow-hidden">
      <div
        ref="mapContainer"
        class="h-full w-full rounded-lg shadow-md"
      ></div>
    </div>
    <template #footer>
      <Button
        class="w-full"
        label="Accept Location"
        icon="pi pi-check"
        @click="closeDialog"
      />
    </template>
  </Dialog>
</template>

<script setup>
  import { ref, watch, onMounted, nextTick } from "vue";

  // Props
  const props = defineProps({
    visible: {
      type: Boolean,
      required: true
    },
    latitude: {
      type: Number,
      required: false,
      default: null
    },
    longitude: {
      type: Number,
      required: false,
      default: null
    },
    title: {
      type: String,
      default: "Location Map"
    },
    zoom: {
      type: Number,
      default: 15
    },
    clickable: {
      type: Boolean,
      default: false
    }
  });

  // Emits
  const emit = defineEmits(["update:visible", "coordinates-updated"]);

  // Reactive refs
  const localVisible = ref(props.visible);
  const mapContainer = ref(null);
  let map = null;
  let marker = null;
  let currentLat = ref(props.latitude);
  let currentLng = ref(props.longitude);

  // Watch the visible prop and update localVisible accordingly
  watch(
    () => props.visible,
    (newVal) => {
      localVisible.value = newVal;
      if (newVal) {
        nextTick(() => {
          initializeMap();
        });
      }
    },
    { immediate: true }
  );

  // Watch localVisible and emit changes to the parent
  watch(
    () => localVisible.value,
    (newVal) => {
      // Emit final coordinates when dialog is closed
      if (!newVal && marker && props.clickable) {
        const position = marker.position;
        emit("coordinates-updated", {
          latitude: position.lat,
          longitude: position.lng
        });
      }
      emit("update:visible", newVal);
    }
  );

  const closeDialog = () => {
    localVisible.value = false;
  };

  const initializeMap = async () => {
    if (!mapContainer.value) {
      return;
    }

    // Load Google Maps API if not already loaded
    if (!window.google || !window.google.maps) {
      if (window.loadGoogleMapsAPI) {
        try {
          await window.loadGoogleMapsAPI();
        } catch (error) {
          console.error("Failed to load Google Maps API:", error);
          return;
        }
      } else {
        console.warn("Google Maps API not loaded and loadGoogleMapsAPI not available");
        return;
      }
    }

    // Check if marker library is available
    if (
      !window.google.maps.marker ||
      !window.google.maps.marker.AdvancedMarkerElement
    ) {
      console.error(
        "Google Maps Marker library not loaded. AdvancedMarkerElement is not available."
      );
      return;
    }

    // Default center (can be customized)
    const defaultCenter = { lat: 40.7128, lng: -74.006 }; // New York City

    // Use provided coordinates if available
    const center =
      props.latitude && props.longitude
        ? { lat: props.latitude, lng: props.longitude }
        : defaultCenter;

    // Update current coordinates
    currentLat.value = props.latitude;
    currentLng.value = props.longitude;

    // Create map
    map = new window.google.maps.Map(mapContainer.value, {
      center: center,
      zoom: props.zoom,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true,
      mapId: import.meta.env.VITE_GMAPS_MAP_ID
    });

    // Add marker if coordinates are provided
    if (props.latitude && props.longitude) {
      createMarker({ lat: props.latitude, lng: props.longitude });
    }

    // Add click listener to map if clickable is enabled
    if (props.clickable) {
      map.addListener("click", (event) => {
        const clickedPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        createMarker(clickedPosition);

        // Update current coordinates
        currentLat.value = clickedPosition.lat;
        currentLng.value = clickedPosition.lng;
      });
    }
  };

  const createMarker = (position) => {
    // Remove existing marker if any
    if (marker) {
      marker.map = null;
    }

    // Create info window content
    const infoWindowContent = `
    <div style="padding: 10px;">
      <h3 style="margin: 0 0 5px 0; font-size: 14px;">Location</h3>
      <p style="margin: 0; font-size: 12px;">
        Latitude: ${position.lat.toFixed(6)}<br>
        Longitude: ${position.lng.toFixed(6)}
      </p>
      ${props.clickable ? '<p style="margin: 5px 0 0 0; font-size: 11px; color: #666;">Tap/click on map to move pin</p>' : ""}
    </div>
  `;

    // Create info window
    const infoWindow = new window.google.maps.InfoWindow({
      content: infoWindowContent
    });

    // Create AdvancedMarkerElement (not draggable)
    marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: position,
      map: map,
      title: "Location",
      gmpDraggable: false
    });

    // Add click event listener to show info window
    marker.addListener("click", () => {
      infoWindow.open(map, marker);
    });
  };

  // Watch for changes in coordinates
  watch(
    () => [props.latitude, props.longitude],
    ([newLat, newLng]) => {
      if (map && newLat && newLng) {
        const newPosition = { lat: newLat, lng: newLng };

        // Update map center
        map.setCenter(newPosition);

        // Update current coordinates
        currentLat.value = newLat;
        currentLng.value = newLng;

        // Create or update marker
        createMarker(newPosition);
      }
    }
  );

  // Watch for changes in clickable prop
  watch(
    () => props.clickable,
    (newClickable) => {
      if (map) {
        // Remove existing click listeners
        window.google.maps.event.clearListeners(map, "click");

        // Add click listener if clickable is enabled
        if (newClickable) {
          map.addListener("click", (event) => {
            const clickedPosition = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            };
            createMarker(clickedPosition);

            // Update current coordinates
            currentLat.value = clickedPosition.lat;
            currentLng.value = clickedPosition.lng;
          });
        }
      }
    }
  );
</script>
